import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { getConciergeRequestCodeAllowedUserIds } from '../../api/noir';
import './CodeFormModal.scss';

// Parse a free-form user-ID list (commas, spaces, and/or newlines) into a deduped
// numeric array, surfacing invalid tokens and how many duplicates were dropped.
const parseUserIdInput = raw => {
  const tokens = (raw || '')
    .split(/[\s,;]+/)
    .map(t => t.trim())
    .filter(Boolean);
  const invalid = tokens.filter(t => !/^\d+$/.test(t));
  const numeric = tokens.filter(t => /^\d+$/.test(t)).map(t => parseInt(t, 10));
  const ids = [...new Set(numeric)];
  return { ids, invalid, duplicates: numeric.length - ids.length };
};

const CodeFormModal = ({ header, initial, isEditing, onCancel, onSubmit }) => {
  const [code, setCode] = useState(initial?.code || '');
  const [requestsGrantedPerRedemption, setRequestsGrantedPerRedemption] = useState(initial?.requestsGrantedPerRedemption ?? 5);
  const [perUserLimit, setPerUserLimit] = useState(initial?.perUserLimit ?? '');
  const [totalLimit, setTotalLimit] = useState(initial?.totalLimit ?? '');
  const [userIds, setUserIds] = useState('');
  const [loadingUserIds, setLoadingUserIds] = useState(!!(isEditing && initial?.isGated));
  const [enableQuickSetup, setEnableQuickSetup] = useState(!!initial?.enableQuickSetup);
  const [expiresAt, setExpiresAt] = useState(initial?.expiresAt ? moment(initial.expiresAt).format('YYYY-MM-DD') : '');
  const codeRef = useRef(null);

  useEffect(() => {
    codeRef.current?.focus();
  }, []);

  // Gated codes store their allow-list in the redemptions table, so load it lazily to
  // prefill the textarea when editing.
  useEffect(() => {
    if (!isEditing || !initial?.isGated) return;
    let active = true;
    getConciergeRequestCodeAllowedUserIds(initial.code)
      .then(ids => active && setUserIds(ids.join(', ')))
      .catch(err => window.ALERT.error(err || 'Failed to load gated users.'))
      .finally(() => active && setLoadingUserIds(false));
    return () => {
      active = false;
    };
  }, [isEditing, initial]);

  const handleSubmit = () => {
    const trimmedCode = code.trim().toUpperCase();
    if (!trimmedCode) return window.ALERT.error('Code is required.');
    if (!requestsGrantedPerRedemption || requestsGrantedPerRedemption < 1) return window.ALERT.error('Requests per use must be at least 1.');
    if (perUserLimit !== '' && perUserLimit < 1) return window.ALERT.error('Per-user limit must be at least 1.');
    if (totalLimit !== '' && totalLimit < 1) return window.ALERT.error('Total limit must be at least 1.');

    const { ids, invalid } = parseUserIdInput(userIds);
    if (invalid.length) {
      return window.ALERT.error(`These User IDs aren't numeric: ${invalid.slice(0, 5).join(', ')}${invalid.length > 5 ? ` (+${invalid.length - 5} more)` : ''}`);
    }

    onSubmit({
      code: trimmedCode,
      requestsGrantedPerRedemption: parseInt(requestsGrantedPerRedemption, 10),
      perUserLimit: perUserLimit === '' ? null : parseInt(perUserLimit, 10),
      totalLimit: totalLimit === '' ? null : parseInt(totalLimit, 10),
      enableQuickSetup,
      expiresAt: expiresAt || null,
      User_ids: ids
    });
  };

  const { ids: previewIds, invalid: previewInvalid, duplicates: previewDuplicates } = parseUserIdInput(userIds);

  return (
    <div className='code-form-modal react-confirm-alert-body'>
      <h1>{header}</h1>
      <div className='code-form-fields'>
        <label>
          Code
          <input ref={codeRef} type='text' value={code} disabled={isEditing} onChange={e => setCode(e.target.value)} placeholder='SUMMER10' />
        </label>
        <label>
          Requests per use
          <input type='number' value={requestsGrantedPerRedemption} onChange={e => setRequestsGrantedPerRedemption(e.target.value)} min={1} />
        </label>
        <label>
          Per-user limit <span className='optional'>optional — max redemptions per user</span>
          <input type='number' value={perUserLimit} onChange={e => setPerUserLimit(e.target.value)} min={1} placeholder='No limit' />
        </label>
        <label>
          Total limit <span className='optional'>optional — max redemptions across everyone</span>
          <input type='number' value={totalLimit} onChange={e => setTotalLimit(e.target.value)} min={1} placeholder='No limit' />
        </label>
        <label>
          Restrict to User IDs <span className='optional'>optional</span>
          <textarea
            className='user-ids-input'
            rows={6}
            value={userIds}
            disabled={loadingUserIds}
            onChange={e => setUserIds(e.target.value)}
            placeholder={loadingUserIds ? 'Loading gated users…' : 'Paste User IDs separated by commas, spaces, or new lines — e.g. 123, 456 789. Leave empty for all users.'}
          />
          <div className='user-ids-hint'>
            {previewIds.length ? (
              <span>
                {previewIds.length.toLocaleString()} user{previewIds.length !== 1 ? 's' : ''} will be gated
              </span>
            ) : (
              <span>No IDs — code will be open to all users</span>
            )}
            {previewDuplicates > 0 && <span className='warn'> · {previewDuplicates} duplicate{previewDuplicates !== 1 ? 's' : ''} removed</span>}
            {previewInvalid.length > 0 && (
              <span className='error'>
                {' '}
                · invalid: {previewInvalid.slice(0, 5).join(', ')}
                {previewInvalid.length > 5 ? ` (+${previewInvalid.length - 5} more)` : ''}
              </span>
            )}
          </div>
        </label>
        <label>
          Expires on <span className='optional'>optional</span>
          <input type='date' value={expiresAt} onChange={e => setExpiresAt(e.target.value)} />
        </label>
        <label className='checkbox-row'>
          <input type='checkbox' checked={enableQuickSetup} onChange={e => setEnableQuickSetup(e.target.checked)} />
          Allow Noir SMS quick setup
        </label>
      </div>
      <div className='react-confirm-alert-button-group'>
        <button className='cancel-btn' onClick={onCancel}>
          Cancel
        </button>
        <button onClick={handleSubmit} disabled={loadingUserIds}>
          Save
        </button>
      </div>
    </div>
  );
};

CodeFormModal.propTypes = {
  header: PropTypes.string.isRequired,
  initial: PropTypes.object,
  isEditing: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired
};

export default CodeFormModal;
